import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Notification from '../components/Notification';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    message: '',
    type: 'info',
    isVisible: false
  });

  const showNotification = useCallback((message, type = 'info', duration = 5000) => {
    setNotification({
      message,
      type,
      isVisible: true
    });

    // Auto-hide after duration
    if (duration > 0) {
      setTimeout(() => {
        hideNotification();
      }, duration);
    }
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({
      ...prev,
      isVisible: false
    }));
  }, []);

  const showSuccess = useCallback((message, duration = 5000) => {
    showNotification(message, 'success', duration);
  }, [showNotification]);

  const showError = useCallback((message, duration = 5000) => {
    showNotification(message, 'error', duration);
  }, [showNotification]);

  const showWarning = useCallback((message, duration = 5000) => {
    showNotification(message, 'warning', duration);
  }, [showNotification]);

  const showInfo = useCallback((message, duration = 5000) => {
    showNotification(message, 'info', duration);
  }, [showNotification]);

  // Special function for password notifications - longer duration
  const showPasswordSuccess = useCallback((message, duration = 30000) => {
    showNotification(message, 'success', duration);
  }, [showNotification]);

  // Listen for custom notification events from iframe service
  useEffect(() => {
    const handleCustomNotification = (event) => {
      const { type, message } = event.detail;
      showNotification(message, type, 8000); // Show for 8 seconds for entry processing
    };

    window.addEventListener('show-notification', handleCustomNotification);
    
    return () => {
      window.removeEventListener('show-notification', handleCustomNotification);
    };
  }, [showNotification]);

  const value = {
    notification,
    showNotification,
    hideNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showPasswordSuccess
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </NotificationContext.Provider>
  );
};
